import { calculateAge } from "features/patients/utils";

import { diagnosisItems } from "../constants/diagnosisItems";

// PP registration-reason visibility rules (HSE CDM spec / Investigations Matrix):
//
// - QRISK3 / Pre-Diabetes / Previous BNP are "OCF referral" reasons (11a) — they
//   reach PP ONLY via a prior OCF outcome, never as a direct-entry (11b) pick.
//   They are therefore offered only when carried in from an OCF (or already on
//   the review) — see the alreadySelectedIds early-return below — and are never
//   selectable on a fresh direct PP registration.
// - Hypertension (all stages) has direct PP access for GMS / HAA / DVC card
//   holders only — hidden for private / non-GMS patients.
// - Gestational Diabetes / Pre-Eclampsia are pregnancy conditions ("all women"
//   in the spec) — hidden for male patients and offered only over 18 years
//   ("GMS, DVC, HAA & Private Patients >18 years" in the GP Contract v3-2 spec).
//
// On edit, any reason already selected on the review stays visible so an
// existing value is never silently dropped.
const PREGNANCY_MIN_AGE = 18;
export const OCF_DERIVED_IDS = [
  "QRISK_GTE_20",
  "PRE_DIABETES",
  "PREVIOUS_HIGH_BNP",
];
const HYPERTENSION_IDS = [
  "HYPERTENSION_STAGE_1_NO_TARGET_ORGAN_DAMAGE",
  "HYPERTENSION_STAGE_1_WITH_TARGET_ORGAN_DAMAGE",
  "HYPERTENSION_STAGE_2_OR_3",
];
const PREGNANCY_IDS = ["GESTATIONAL_DIABETES", "PRE_ECLAMPSIA"];
// GMS (02), Temporary GMS (04), Doctor Only Visit Card (05), Temp DV Card (06).
const GMS_DVC_CLASSES = ["02", "04", "05", "06"];

// Patient gender is free text and reaches us as "Female" / "FEMALE" / "Male" /
// "MALE" / "Unknown" / "Unspecified" / null / "" — never a single letter. So
// match on the first letter, as the backend does (CdmCoreService.isFemale,
// itself following PID.8 / IHI), rather than comparing to a code that never
// occurs.
const isFemale = (gender) =>
  typeof gender === "string" && gender.trim().toUpperCase().startsWith("F");

// Gestational Diabetes / Pre-Eclampsia are pregnancy conditions ("all women"
// in the spec) — offered only to female patients over 18 ("GMS, DVC, HAA &
// Private Patients >18 years", GP Contract v3-2). Shared across CDM/OCF/PP so
// these reasons are gated identically wherever they appear.
//
// Gated on being female rather than on not being male: gender is optional on a
// patient record, and "all women" does not include a patient we cannot tell is
// one. An unknown-gender patient therefore has to have their gender recorded
// before these reasons appear, which is also what the backend requires of a
// private patient before it will accept the registration.
export const isPregnancyConditionVisible = (patient) => {
  const age = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : null;

  return isFemale(patient?.gender) && age !== null && age > PREGNANCY_MIN_AGE;
};

// Visibility rule for a single PP reason id. Shared by the registration reasons
// AND the additional-risk-factor table so both show/hide identically:
// - an already-selected id always stays visible (never silently dropped);
// - OCF-referral reasons (11a) are never offered as a fresh direct-entry pick;
// - Hypertension has direct access only for GMS / HAA / DVC card holders;
// - Gestational Diabetes / Pre-Eclampsia only for female patients over 18.
export const isPpReasonVisible = ({
  id,
  patient,
  haaNumber,
  alreadySelectedIds = [],
}) => {
  if (alreadySelectedIds.includes(id)) return true;

  if (OCF_DERIVED_IDS.includes(id)) return false;

  if (HYPERTENSION_IDS.includes(id)) {
    return GMS_DVC_CLASSES.includes(patient?.financialClass) || !!haaNumber;
  }

  if (PREGNANCY_IDS.includes(id)) return isPregnancyConditionVisible(patient);

  return true;
};

export const getVisiblePpReasons = ({
  patient,
  haaNumber,
  alreadySelectedIds = [],
}) =>
  diagnosisItems.filter((item) =>
    isPpReasonVisible({ id: item.id, patient, haaNumber, alreadySelectedIds })
  );

// Why a reason is not offered to this patient. Hidden rows were previously just
// absent, leaving no way to tell an ineligible patient from a missing option —
// the rule is spelled out instead of leaving the GP to infer it.
const getPpReasonUnavailableReason = ({ id, patient }) => {
  if (OCF_DERIVED_IDS.includes(id)) {
    return "Only available when carried over from an Opportunistic Case Finding outcome — it cannot be selected on a direct registration.";
  }

  if (HYPERTENSION_IDS.includes(id)) {
    return "Direct registration for hypertension is available to GMS, Doctor Only Visit Card and HAA cardholders only.";
  }

  if (PREGNANCY_IDS.includes(id)) {
    const age = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : null;

    // Both halves of the rule can fail at once; name the one the patient
    // actually fails, so the GP is not sent to check the wrong field.
    if (!isFemale(patient?.gender)) {
      return "Available to female patients only. Check the patient's gender is recorded if this looks wrong.";
    }

    return `Available to patients over ${PREGNANCY_MIN_AGE} years${
      age === null ? " — this patient has no date of birth recorded." : "."
    }`;
  }

  return null;
};

export const getUnavailablePpReasons = ({
  patient,
  haaNumber,
  alreadySelectedIds = [],
}) =>
  diagnosisItems
    .filter(
      (item) =>
        !isPpReasonVisible({
          id: item.id,
          patient,
          haaNumber,
          alreadySelectedIds,
        })
    )
    .map((item) => ({
      id: item.id,
      name: item.name,
      reason: getPpReasonUnavailableReason({ id: item.id, patient }),
    }))
    .filter((item) => !!item.reason);
